import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';

export interface DismissScrimProps {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

/*
 * Escape reaches the innermost open layer only, so a modal raised from an open pane does not
 * collapse the pane under it. Mount order gives that for free - the modal always registers after
 * the pane it sits in - where document listener order does not, so the stack is explicit.
 */
const stack: Array<() => void> = [];

function onKeyDown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || stack.length === 0) return;
  stack[stack.length - 1]();
}

function push(entry: () => void): void {
  if (stack.length === 0) document.addEventListener('keydown', onKeyDown, false);
  stack.push(entry);
}

function pop(entry: () => void): void {
  const at = stack.indexOf(entry);
  if (at >= 0) stack.splice(at, 1);
  if (stack.length === 0) document.removeEventListener('keydown', onKeyDown, false);
}

/**
 * Tap-outside dismissal for every layer that closes by touching its scrim.
 *
 * One pointer has to go down AND come up on the scrim itself. pointerdown alone fires the moment a
 * finger lands, so a scroll that starts on the glass closes the layer, and the click that follows
 * lands on whatever the scrim was covering. The pair also reads a stylus and a finger the same way:
 * touch takes implicit pointer capture, so a finger that drifts onto the pane still reports the
 * scrim as its pointerup target, while a drag that began inside the pane never arms at all.
 */
export function useDismissable(open: boolean, onDismiss: () => void): DismissScrimProps {
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  // the pointer that went down on the scrim; -1 when none is armed
  const armed = useRef(-1);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    armed.current = -1;
    const entry = () => dismissRef.current();
    push(entry);
    return () => pop(entry);
  }, [open]);

  return {
    onPointerDown: (event) => {
      armed.current = event.target === event.currentTarget ? event.pointerId : -1;
    },
    onPointerUp: (event) => {
      const hit = armed.current === event.pointerId && event.target === event.currentTarget;
      armed.current = -1;
      if (hit) dismissRef.current();
    },
    onPointerCancel: () => { armed.current = -1; },
  };
}

export default useDismissable;
